import { useApolloClient } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { DocumentContentDocument } from "@/graphql/generated/operations";
import { userFacingGraphqlError } from "@/lib/graphql-error";

export function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

/**
 * Öffnet ein DOCUMENTS-Dokument als Blob in einem neuen Tab.
 * Fehler laufen über den onError-Callback (Anzeige beim Aufrufer, ADR-0010).
 */
export function useOpenDocument(onError: (message: string) => void) {
  const intl = useIntl();
  const apollo = useApolloClient();

  return async function openDocument(id: string) {
    // Open the tab synchronously inside the click gesture: after the awaited
    // fetch, browsers treat window.open as an unsolicited pop-up and block it.
    // (No "noopener" here - that makes window.open return null even when the
    // tab DID open, so we couldn't tell it apart from a blocked pop-up. The
    // tab only ever navigates to our own blob URL.)
    const win = window.open("", "_blank");
    if (win) win.document.title = intl.formatMessage({ id: "service.page.docOpening", defaultMessage: "Document loading …" });
    try {
      const { data: docData } = await apollo.query({
        query: DocumentContentDocument,
        variables: { id },
        fetchPolicy: "network-only",
      });
      const file = docData?.document?.content;
      if (!file?.content) throw new Error(intl.formatMessage({ id: "service.page.errNoContent", defaultMessage: "No content available." }));
      const blob = base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
      const url = URL.createObjectURL(blob);
      if (win) {
        win.location.href = url;
      } else {
        onError(
          intl.formatMessage({ id: "service.page.errPopupBlocked", defaultMessage: "The browser blocked opening in a new tab - please allow pop-ups for this site and click Open again." }),
        );
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      win?.close();
      onError(userFacingGraphqlError(e, intl.formatMessage({ id: "service.page.errOpen", defaultMessage: "Opening failed." })));
    }
  };
}
